import { useState } from "react";
import { ChevronDown, Check, X, Shirt, Sparkles } from "lucide-react";

const faqs = [
  {
    q: "What counts as 'all white'?",
    a: "Head-to-toe white is the standard. Ivory, cream, and off-white are welcome. Shoes, accessories, and clutches can include pops of color — but your outfit should read white.",
  },
  {
    q: "Can I wear patterns or prints?",
    a: "Subtle white-on-white textures and patterns are fine (lace, embroidery, jacquard). Avoid large logos, bold graphics, or multi-color prints.",
  },
  {
    q: "Are jeans or sneakers allowed?",
    a: "White denim can work if styled sharp. Clean white sneakers are acceptable.",
  },
];

type FaqStyle = "accordion" | "cards" | "dosdonts" | "editorial";

const styleOptions: { key: FaqStyle; label: string }[] = [
  { key: "accordion", label: "Classic" },
  { key: "cards", label: "Cards" },
  { key: "dosdonts", label: "Do's & Don'ts" },
  { key: "editorial", label: "Editorial" },
];

// ============================================
// 1. ACCORDION — Classic expandable (current, refined)
// ============================================
const AccordionStyle = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <div className="max-w-[800px] mx-auto">
      <div className="border-t border-ink/10">
        {faqs.map((faq, i) => (
          <div key={i} className="border-b border-ink/10">
            <button
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              className="w-full flex items-center justify-between py-6 sm:py-8 text-left gap-4 group"
            >
              <div className="flex items-center gap-4">
                <span className="font-display text-2xl sm:text-3xl text-gold/30 group-hover:text-gold transition-colors">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  className="font-heading uppercase text-foreground group-hover:text-gold transition-colors"
                  style={{
                    fontSize: "var(--body-size, 1rem)",
                    fontWeight: "var(--label-weight, 800)",
                    letterSpacing: "var(--label-tracking, 0.12em)",
                  }}
                >
                  {faq.q}
                </span>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-ink/30 shrink-0 transition-transform duration-300 ${openIndex === i ? "rotate-180 text-gold" : ""}`}
              />
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-out ${openIndex === i ? "max-h-48 pb-8" : "max-h-0"}`}
            >
              <p
                className="font-serif-elegant text-foreground/60 italic pl-[52px] sm:pl-[60px] max-w-[90%]"
                style={{
                  fontSize: "var(--body-size, 1.2rem)",
                  lineHeight: "var(--body-line-height, 1.65)",
                }}
              >
                {faq.a}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// 2. CARDS — Grid of styled cards, always visible
// ============================================
const CardsStyle = () => (
  <div className="max-w-[1000px] mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
    {faqs.map((faq, i) => (
      <div
        key={i}
        className="bg-white border border-ink/10 p-6 sm:p-8 relative overflow-hidden group hover:border-gold/30 transition-colors"
      >
        {/* Corner accent */}
        <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-gold/10 to-transparent" />
        <div className="flex items-start gap-3 mb-4">
          <Shirt className="w-5 h-5 text-gold shrink-0 mt-0.5" />
          <h3
            className="font-heading uppercase text-foreground"
            style={{
              fontSize: "var(--body-size, 1rem)",
              fontWeight: "var(--label-weight, 700)",
              letterSpacing: "var(--label-tracking, 0.1em)",
            }}
          >
            {faq.q}
          </h3>
        </div>
        <p
          className="font-serif-elegant text-foreground/60 italic"
          style={{
            fontSize: "var(--body-size, 1.1rem)",
            lineHeight: "var(--body-line-height, 1.65)",
          }}
        >
          {faq.a}
        </p>
      </div>
    ))}
  </div>
);

// ============================================
// 3. DO'S & DON'TS — Split column visual guide
// ============================================
const dosList = [
  "Head-to-toe white, ivory, cream, or off-white",
  "White-on-white textures (lace, embroidery, jacquard)",
  "White denim styled sharp",
  "Clean white sneakers or dress shoes",
  "Metallic gold or silver accessories",
  "White jacket or blazer for cool weather",
];

const dontsList = [
  "Colors, large logos, or bold graphics",
  "Multi-color prints or patterns",
  "Casual beachwear or flip flops",
  "Anything that doesn't read white",
];

const DosDontsStyle = () => (
  <div className="max-w-[900px] mx-auto">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
      {/* DO's */}
      <div className="bg-white border border-ink/10 p-6 sm:p-10">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <Check className="w-4 h-4 text-green-700" />
          </div>
          <h3
            className="font-heading uppercase text-green-800"
            style={{
              fontSize: "var(--body-size, 1.1rem)",
              fontWeight: "var(--label-weight, 800)",
              letterSpacing: "var(--label-tracking, 0.15em)",
            }}
          >
            Yes
          </h3>
        </div>
        <ul className="space-y-4">
          {dosList.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <Check className="w-4 h-4 text-green-500 shrink-0 mt-1" />
              <span
                className="font-serif-elegant text-foreground/70 italic"
                style={{
                  fontSize: "var(--body-size, 1.1rem)",
                  lineHeight: "var(--body-line-height, 1.6)",
                }}
              >
                {item}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* DON'Ts */}
      <div className="bg-ink text-cream p-6 sm:p-10">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
            <X className="w-4 h-4 text-red-400" />
          </div>
          <h3
            className="font-heading uppercase text-red-300"
            style={{
              fontSize: "var(--body-size, 1.1rem)",
              fontWeight: "var(--label-weight, 800)",
              letterSpacing: "var(--label-tracking, 0.15em)",
            }}
          >
            No
          </h3>
        </div>
        <ul className="space-y-4">
          {dontsList.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <X className="w-4 h-4 text-red-400/60 shrink-0 mt-1" />
              <span
                className="font-serif-elegant text-cream/60 italic"
                style={{
                  fontSize: "var(--body-size, 1.1rem)",
                  lineHeight: "var(--body-line-height, 1.6)",
                }}
              >
                {item}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    {/* Pro tip */}
    <div className="bg-gold/10 border border-gold/20 p-5 sm:p-6 flex items-start gap-3">
      <Sparkles className="w-5 h-5 text-gold shrink-0 mt-0.5" />
      <p
        className="font-serif-elegant text-foreground/70 italic"
        style={{
          fontSize: "var(--body-size, 1.1rem)",
          lineHeight: "var(--body-line-height, 1.6)",
        }}
      >
        Think upscale lounge, not beach party. Blazers, cocktail dresses, linen sets — polished and intentional.
      </p>
    </div>
  </div>
);

// ============================================
// 4. EDITORIAL — Magazine-style with large type
// ============================================
const EditorialStyle = () => (
  <div className="max-w-[900px] mx-auto">
    {/* Big statement */}
    <div className="text-center mb-12 sm:mb-16">
      <p
        className="font-serif-elegant italic text-foreground/80 max-w-xl mx-auto"
        style={{
          fontSize: "clamp(1.5rem, 3vw, 2.5rem)",
          lineHeight: 1.4,
        }}
      >
        "Head-to-toe white. Ivory, cream, and off-white are welcome. Think sophisticated — not casual."
      </p>
    </div>

    {/* Horizontal rules with tips between */}
    <div className="space-y-0">
      {faqs.map((faq, i) => (
        <div key={i} className="border-t border-ink/10 py-8 sm:py-10 grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4 sm:gap-8">
          <div>
            <span
              className="font-heading uppercase text-gold"
              style={{
                fontSize: "var(--label-size, 0.85rem)",
                fontWeight: "var(--label-weight, 800)",
                letterSpacing: "var(--label-tracking, 0.2em)",
              }}
            >
              {faq.q.replace("?", "")}
            </span>
          </div>
          <p
            className="font-serif-elegant text-foreground/70 italic"
            style={{
              fontSize: "var(--body-size, 1.25rem)",
              lineHeight: "var(--body-line-height, 1.7)",
            }}
          >
            {faq.a}
          </p>
        </div>
      ))}
      <div className="border-t border-ink/10" />
    </div>
  </div>
);

// ============================================
// Main component with switcher
// ============================================
const DressCodeFaq = () => {
  return (
    <section data-nav-theme="light" className="bg-cream py-12 sm:py-[8vw]">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-[4vw]">
        <div className="mb-8 sm:mb-12 text-center sm:text-left">
          <p
            className="font-heading uppercase text-foreground mb-2"
            style={{
              fontSize: "var(--label-size, 0.85rem)",
              fontWeight: "var(--label-weight, 800)",
              letterSpacing: "var(--label-tracking, 0.2em)",
            }}
          >
            Dress Code Guide
          </p>
          <h2
            className="font-serif-elegant text-foreground"
            style={{
              fontSize: "var(--section-size, clamp(1.75rem, 3.5vw, 3rem))",
              fontWeight: "var(--section-weight, 400)",
              fontStyle: "var(--section-style, italic)" as any,
              letterSpacing: "var(--section-tracking, 0)",
              lineHeight: 1.2,
            }}
          >
            What to Wear
          </h2>
        </div>

        <AccordionStyle />
      </div>
    </section>
  );
};

export default DressCodeFaq;
